import React, { memo, useCallback, useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { DateTime } from "luxon"

import { Box, Text } from "@mos-cat/ds"
import { useZoneContext } from "@components/contexts/ZoneContext"
import { EditorPreview, stringToEditorContent } from "@components/form"
import {
  PageBlockRow,
  PageBlockRowSection,
  PageBlockContent,
  PageBlockHeading,
  // StarIcon,
  Avatar,
  getFormattedRecentDate,
  CustomGetUsersOptionLabel,
  Button,
  Favorites,
  CircleCrossIcon,
  Tooltip,
  useAppContext,
} from "@dit/core-frontend"
import TemplateActions from "./TemplateActions"

import { ROUTES, API_ENDPOINTS } from "@constants"
import { ENTITY_TYPES } from "@utils/favorites"
import styles from "@components/pages/pagePreview.module.css"
import cc from "classcat"

export const TemplatePagePreview = memo(
  ({ template, openEditTemplateCurtain, updateTemplateData }) => {
    const { zone } = useZoneContext()
    const navigate = useNavigate()
    const { isMobileView } = useAppContext()
    const [scrollHeight, setScrollHeight] = useState(0)
    const [isPanelShow, setIsPanelShow] = useState(true)

    const handleNavigation = useCallback(
      (e) => {
        const window = e.target
        if (window.scrollTop > 100) {
          if (scrollHeight > window.scrollTop) {
            setIsPanelShow(true)
          } else if (scrollHeight < window.scrollTop) {
            setIsPanelShow(false)
          }
          setScrollHeight(window.scrollTop)
        }
      },
      [scrollHeight],
    )

    useEffect(() => {
      const scrollElem = document.getElementById("mainContainer")
      setScrollHeight(scrollElem.scrollTop)
      scrollElem.addEventListener("scroll", handleNavigation)

      return () => {
        scrollElem.removeEventListener("scroll", handleNavigation)
      }
    }, [handleNavigation])

    return (
      <>
        <PageBlockRow
          place="middle"
          position="sticky"
          top="-40px"
          zIndex="10"
          alignItems="center"
          backgroundColor="white"
          m={["0 -10px!important", "0 -20px!important", "0 -20px 0 -40px!important"]}
          p={["10px", "20px", "10px 20px 10px 40px"]}
          width="auto"
          borderBottom="1px solid #EEEFF2"
          display="flex"
          flexDirection={["column-reverse", "row"]}
          flexWrap={["nowrap", "nowrap", "nowrap", "wrap"]}
          className={cc({
            [styles.topPanel]: true,
            [styles.show]: isPanelShow,
            [styles.hide]: !isPanelShow,
          })}
        >
          <PageBlockRowSection
            overflow="hidden"
            flexDirection={["column-reverse", "column"]}
            alignItems="flex-start"
            gridGap="10px"
            width={["100%", "auto", "auto"]}
          >
            <Box display="flex" alignItems="center" gridGap="10px">
              <Box display="flex" alignItems="center" gridGap="5px">
                <Avatar size={20} url={template.user?.photo?.url} />
                <Text fontSize="14px" color="gray-80">
                  {CustomGetUsersOptionLabel(template.user)}
                </Text>
              </Box>
              <Text fontSize="14px" color="gray-40">
                {getFormattedRecentDate(
                  DateTime.fromISO(template.modifiedAt || template.createdAt),
                )}
              </Text>
            </Box>
          </PageBlockRowSection>
          <Box
            display="flex"
            flexWrap="wrap"
            gridGap={["5px", "15px", "15px", "20px"]}
            width={["100%", "auto", "auto"]}
            justifyContent={["flex-end", "flex-end", "flex-end", "space-between"]}
          >
            <Box
              display="flex"
              alignItems="center"
              padding="4px 10px 4px 10px"
              borderRadius="4px"
              backgroundColor="var(--warning-10)"
            >
              <Text fontSize="14px" color="var(--warning-70)">
                {template.title}
              </Text>
            </Box>
            <Box display="flex" alignItems="center" gridGap="5px">
              <Favorites
                itemId={template.id}
                favoritesId={template.favorites?.id}
                type={ENTITY_TYPES.TEMPLATE}
                requestUrl={API_ENDPOINTS.favorites}
                iconSize={21}
                p="5px !important"
                kind="gray"
                isButtonView
              />
              <TemplateActions
                template={template}
                zone={zone}
                as={Button}
                kind="gray"
                p="0 !important"
                onDelete={() => {
                  navigate(`${ROUTES.ZONE}/${zone.slug}/templates`, { replace: true })
                }}
                openEditTemplateCurtain={openEditTemplateCurtain}
                onUpdate={updateTemplateData}
              />
              <Tooltip content="Закрыть" disabled={isMobileView}>
                <Link to={`${ROUTES.ZONE}/${zone.slug}/templates`}>
                  <Button kind="gray" padding="5px!important">
                    <CircleCrossIcon size={22} />
                  </Button>
                </Link>
              </Tooltip>
            </Box>
          </Box>
        </PageBlockRow>
        <PageBlockContent style={{ width: "100%", maxWidth: "1200px" }}>
          <PageBlockHeading styles={{ maxWidth: "100%", marginBottom: "20px" }}>
            <Text
              fontSize={["24px", "34px", "44px"]}
              lineHeight={["28px", "38px", "48px"]}
              className={styles.title}
            >
              {template?.pageTitle}
            </Text>
          </PageBlockHeading>
          <EditorPreview content={stringToEditorContent(template.content || "")} />
        </PageBlockContent>
      </>
    )
  },
)

TemplatePagePreview.displayName = "TemplatePagePreview"
